// src/pages/admin/Cashflow.jsx
import React, { useState, useMemo } from 'react';
import { useApp } from 'contexts/AppContext';
import { Card, Avatar, Badge } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';

const SEL_STYLE = {
  padding:'8px 36px 8px 12px', border:'1.5px solid #E8E5EC', borderRadius:10,
  fontSize:13, outline:'none', appearance:'none', background:'#fff', color:'#3D3447',
  backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
  backgroundRepeat:'no-repeat', backgroundPosition:'right 10px center', minWidth:160,
};

export default function AdminCashflow() {
  const isMobile = useMobile();
  const { appointments, professionals, procedures } = useApp();
  const [period, setPeriod] = useState('month');
  const [filterPro, setFilterPro] = useState('');
  const [filterProc, setFilterProc] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filtered = useMemo(() => {
    const now = new Date();
    return appointments.filter(a => {
      if (!a.done) return false;
      // T12:00 evita erro de fuso (new Date('2026-06-03') = UTC meia-noite → pode virar dia 2 no Brasil)
      const dt = a.dateTime?.toDate?.() || (a.date ? new Date(`${a.date}T12:00:00`) : new Date(0));
      if (period === 'week') {
        const weekAgo = new Date(now); weekAgo.setDate(now.getDate()-7);
        if (dt < weekAgo) return false;
      } else if (period === 'month') {
        if (dt.getMonth()!==now.getMonth() || dt.getFullYear()!==now.getFullYear()) return false;
      } else if (period === 'quarter') {
        const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth()/3)*3, 1);
        if (dt < qStart) return false;
      } else if (period === 'year') {
        if (dt.getFullYear()!==now.getFullYear()) return false;
      }
      if (dateFrom && dt < new Date(dateFrom)) return false;
      if (dateTo && dt > new Date(dateTo+'T23:59:59')) return false;
      if (filterPro && a.professionalId !== filterPro) return false;
      if (filterProc && a.procedureId !== filterProc) return false;
      return true;
    });
  }, [appointments, period, filterPro, filterProc, dateFrom, dateTo]);

  const total = filtered.reduce((s, a) => s + (a.price || 0), 0);
  const periodLabels = { week:'Esta Semana', month:'Este Mês', quarter:'Este Trimestre', year:'Este Ano' };

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>

      {/* Page header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:32, color:'#3D3447' }}>Financeiro</div>
        <div style={{ fontSize:13, color:'#A8A1B0', marginTop:2 }}>
          Receita por atendimentos realizados no período selecionado.
        </div>
      </div>

      {/* Total banner */}
      <div style={{ padding:28, background:'linear-gradient(135deg,#6B5876,#4A3A5A)', borderRadius:20, color:'#fff', marginBottom:24, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', right:24, top:'50%', transform:'translateY(-50%)', fontSize:60, opacity:0.12 }}>💰</div>
        <div style={{ fontSize:11, textTransform:'uppercase', letterSpacing:2, opacity:0.65 }}>Total do Período</div>
        <div style={{ fontFamily:"'Instrument Serif',serif", fontSize: isMobile ? 30 : 48, margin:'8px 0' }}>
          R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
        </div>
        <div style={{ fontSize:13, opacity:0.65 }}>
          {periodLabels[period]} · {filtered.length} atendimento{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center' }}>
        {['week','month','quarter','year'].map(p => (
          <div key={p} onClick={()=>setPeriod(p)}
            style={{ padding:'6px 14px', borderRadius:20, border:`1.5px solid ${period===p?'#6B5876':'#E8E5EC'}`, fontSize:12, fontWeight:500, cursor:'pointer', background:period===p?'#6B5876':'#fff', color:period===p?'#fff':'#6B6070', transition:'all 0.15s' }}>
            {periodLabels[p]}
          </div>
        ))}
        <select value={filterPro} onChange={e=>setFilterPro(e.target.value)} style={SEL_STYLE}>
          <option value="">Todos profissionais</option>
          {professionals.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={filterProc} onChange={e=>setFilterProc(e.target.value)} style={SEL_STYLE}>
          <option value="">Todos procedimentos</option>
          {procedures.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
            style={{ padding:'8px 12px', border:'1.5px solid #E8E5EC', borderRadius:10, fontSize:13, outline:'none', color:'#3D3447' }} />
          <span style={{ color:'#A8A1B0', fontSize:13 }}>até</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
            style={{ padding:'8px 12px', border:'1.5px solid #E8E5EC', borderRadius:10, fontSize:13, outline:'none', color:'#3D3447' }} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'48px 20px', background:'#fff', borderRadius:16, border:'1px solid #E8E5EC' }}>
          <div style={{ fontSize:36, opacity:0.4, marginBottom:8 }}>💸</div>
          <div style={{ fontFamily:"'Instrument Serif',serif", fontSize:18, color:'#3D3447', marginBottom:4 }}>
            Nenhum atendimento no período
          </div>
          <div style={{ fontSize:13, color:'#A8A1B0' }}>
            Marque atendimentos como realizados para que apareçam aqui.
          </div>
        </div>
      ) : isMobile ? (
        /* ── Mobile: cards ─────────────────────────────────── */
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(a => {
            const pro = professionals.find(p=>p.id===a.professionalId);
            const dt  = a.dateTime?.toDate?.() || new Date(a.date);
            return (
              <Card key={a.id}>
                <div style={{ padding:'14px 16px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <Avatar name={a.clientName||'?'} size="sm" />
                      <strong style={{ fontSize:14, color:'#3D3447' }}>{a.clientName}</strong>
                    </div>
                    <span style={{ fontWeight:700, color:'#2F5C42', fontSize:15 }}>
                      R$ {(a.price||0).toLocaleString('pt-BR',{minimumFractionDigits:2})}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'#6B6070', marginBottom:4 }}>💆 {a.procedureName}</div>
                  <div style={{ fontSize:12, color:'#6B6070', marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
                    <Avatar name={pro?.name||'?'} color={pro?.avatarColor||'blue'} size="sm" />
                    {pro?.name}
                  </div>
                  <div style={{ fontSize:12, color:'#A8A1B0', marginTop:6 }}>
                    📅 {dt.toLocaleDateString('pt-BR')} às {a.time || dt.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}
                  </div>
                </div>
              </Card>
            );
          })}
          <div style={{ textAlign:'right', fontWeight:700, color:'#2F5C42', fontSize:16, padding:'8px 4px' }}>
            Total: R$ {total.toLocaleString('pt-BR',{minimumFractionDigits:2})}
          </div>
        </div>
      ) : (
        /* ── Desktop: tabela ────────────────────────────────── */
        <Card>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>
                  {['Data','Horário','Cliente','Procedimento','Profissional','Valor','Status'].map((h,i)=>(
                    <th key={i} style={{ textAlign:'left', padding:'10px 16px', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:'#A8A1B0', borderBottom:'1px solid #E8E5EC', background:'#FAF7FB', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => {
                  const pro = professionals.find(p=>p.id===a.professionalId);
                  const dt  = a.dateTime?.toDate?.() || new Date(a.date);
                  return (
                    <tr key={a.id}>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC', fontWeight:500, color:'#3D3447' }}>
                        {dt.toLocaleDateString('pt-BR')}
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC', color:'#6B6070' }}>
                        {a.time || dt.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})}
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <Avatar name={a.clientName||'?'} size="sm" />
                          <span style={{ color:'#3D3447' }}>{a.clientName}</span>
                        </div>
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC', color:'#6B6070' }}>{a.procedureName}</td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <Avatar name={pro?.name||'?'} color={pro?.avatarColor||'blue'} size="sm" />
                          <span style={{ color:'#6B6070' }}>{pro?.name}</span>
                        </div>
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC', fontWeight:700, color:'#2F5C42', fontSize:14 }}>
                        R$ {(a.price||0).toLocaleString('pt-BR',{minimumFractionDigits:2})}
                      </td>
                      <td style={{ padding:'13px 16px', borderBottom:'1px solid #E8E5EC' }}>
                        <Badge variant="success">✓ Realizado</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', padding:'16px 20px', borderTop:'2px solid #E8E5EC', background:'#FAF7FB', gap:12 }}>
            <span style={{ color:'#6B6070', fontSize:15 }}>
              Total ({filtered.length} registro{filtered.length !== 1 ? 's' : ''}):
            </span>
            <span style={{ fontFamily:"'Instrument Serif',serif", color:'#2F5C42', fontSize:26, fontWeight:700 }}>
              R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </Card>
      )}
    </div>
  );
}
